/**
 * Site geneli ambiyans sesi (ney) — tek sahip (singleton).
 *
 * Neden burada: hem hero'daki VoiceIntro hem de sağ kenardaki SiteAudioBar aynı
 * sesi kontrol ediyor. İki ayrı <audio> elemanı olsaydı ses üst üste binerdi.
 *
 * Otomatik başlatma: tarayıcılar kullanıcı etkileşimi olmadan SESLİ autoplay'i
 * engeller (Chrome/Safari/Firefox — aşılamaz). Bu yüzden önce denenir, engellenirse
 * ilk pointerdown/keydown/touchstart/wheel/scroll olayında kendiliğinden başlar.
 * Kullanıcı kapatırsa tercih localStorage'a yazılır ve bir daha açılmaz.
 */

export const AMBIENT_SRC = '/ses/hiranova-ambiyans.mp3'
export const AMBIENT_VOLUME = 0.14   // normal seviye
export const AMBIENT_DUCKED = 0.04   // anlatım/film oynarken kısılmış seviye
const PREF_KEY = 'hnw_ambient'

type Listener = (state: { ready: boolean; on: boolean }) => void

let el: HTMLAudioElement | null = null
let ready = false
let on = false
let booted = false
let rampId: number | null = null
const listeners = new Set<Listener>()

const emit = () => listeners.forEach((fn) => fn({ ready, on }))

/** Dosya gerçekten var mı? (dev/SPA fallback index.html döndürdüğü için tipe de bakılır) */
const audioExists = async (url: string) => {
    try {
        const r = await fetch(url, { method: 'HEAD' })
        const ct = r.headers.get('content-type') || ''
        return r.ok && /audio|mpeg|octet-stream/i.test(ct)
    } catch {
        return false
    }
}

const element = () => {
    if (!el) {
        el = new Audio(AMBIENT_SRC)
        el.loop = true
        el.preload = 'auto'
        el.volume = AMBIENT_VOLUME
    }
    return el
}

/** Uygulama açılışında bir kez çağrılır. Dosya yoksa hiçbir kontrol görünmez. */
export const bootAmbient = async () => {
    if (booted) return
    booted = true

    ready = await audioExists(AMBIENT_SRC)
    emit()
    if (!ready) return
    if (localStorage.getItem(PREF_KEY) === 'off') return

    const a = element()
    let cleanup: (() => void) | undefined

    const tryPlay = () =>
        a.play().then(() => { on = true; emit(); cleanup?.() }).catch(() => { })

    tryPlay()

    // Autoplay engellendiyse ilk etkileşimi bekle
    const evts: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll']
    const onFirst = () => tryPlay()
    evts.forEach((e) => window.addEventListener(e, onFirst, { once: true, passive: true }))
    cleanup = () => evts.forEach((e) => window.removeEventListener(e, onFirst))
}

export const subscribeAmbient = (fn: Listener) => {
    listeners.add(fn)
    fn({ ready, on })
    return () => { listeners.delete(fn) }
}

export const toggleAmbient = () => {
    if (!ready) return
    const a = element()
    if (on) {
        a.pause()
        on = false
        localStorage.setItem(PREF_KEY, 'off')
    } else {
        a.volume = AMBIENT_VOLUME
        a.play().then(() => { on = true; emit() }).catch(() => { })
        localStorage.removeItem(PREF_KEY)
    }
    emit()
}

/** Ambiyansı hedef seviyeye yumuşak taşı (anlatım/film başlarken kısmak için). */
export const rampAmbient = (to: number) => {
    const a = el
    if (!a || a.paused) return
    const from = a.volume
    const steps = 10
    let i = 0
    if (rampId) window.clearInterval(rampId)
    rampId = window.setInterval(() => {
        i++
        a.volume = Math.max(0, Math.min(1, from + (to - from) * (i / steps)))
        if (i >= steps && rampId) window.clearInterval(rampId)
    }, 60)
}
